'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { marketplaceNav } from '@/config/marketplaceNav';
import { SitePageLayout } from '@/platform/layout/SitePageLayout';
import { siteBrandName } from '@/platform/legal/siteLegal';
import { listMarketplaceVendors, type MarketplaceVendorListing } from '../api';
import { MarketplacePageFrame } from '../MarketplacePageFrame';
import { useMarketplaceSellLabel } from '../useMarketplaceSellLabel';

type Props = {
  /** Product tour: fixture vendors, no API. */
  tourMode?: boolean;
  initialVendors?: MarketplaceVendorListing[];
};

export function MarketplaceDirectoryPage({ tourMode = false, initialVendors }: Props) {
  const brand = siteBrandName();
  const sellLabel = useMarketplaceSellLabel();
  const [vendors, setVendors] = useState<MarketplaceVendorListing[]>(initialVendors ?? []);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(!tourMode);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (tourMode) {
      setVendors(initialVendors ?? []);
      setLoading(false);
      return;
    }
    listMarketplaceVendors()
      .then((r) => {
        setVendors(r.vendors);
        setNextCursor(r.nextCursor);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [tourMode, initialVendors]);

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await listMarketplaceVendors({ cursor: nextCursor });
      setVendors((prev) => [...prev, ...r.vendors]);
      setNextCursor(r.nextCursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load more sellers');
    } finally {
      setLoadingMore(false);
    }
  }

  const body = (
    <section className="feed-card settings-page marketplace-directory">
      {loading && <p className="text-muted">Loading sellers…</p>}
      {error && <p className="form-error">{error}</p>}
      {!loading && !error && vendors.length === 0 && (
        <p className="text-muted marketplace-directory__empty">
          No public storefronts yet. Check back soon or become a seller.
        </p>
      )}
      {vendors.length > 0 && (
        <ul className="marketplace-directory__list">
          {vendors.map((v) => (
            <li key={v.userId} className="marketplace-directory__item">
              {tourMode ? (
                <span className="marketplace-directory__link">
                  <strong>{v.storeName}</strong>
                  {v.tagline && <span className="text-muted"> — {v.tagline}</span>}
                </span>
              ) : (
                <Link href={`/marketplace/sellers/${v.userId}/`} className="marketplace-directory__link">
                  <strong>{v.storeName}</strong>
                  {v.tagline && <span className="text-muted"> — {v.tagline}</span>}
                </Link>
              )}
              {(v.displayName || v.handle) && (
                <p className="text-muted marketplace-directory__seller-meta">
                  {v.displayName}
                  {v.handle ? ` (@${v.handle})` : ''}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {nextCursor && !tourMode && (
        <p className="marketplace-directory__more">
          <button type="button" className="btn btn-ghost" disabled={loadingMore} onClick={() => void loadMore()}>
            {loadingMore ? 'Loading…' : 'Load more sellers'}
          </button>
        </p>
      )}
      {!tourMode && (
        <p>
          <Link href={marketplaceNav.sell.href} className="btn btn-primary">
            {sellLabel}
          </Link>
        </p>
      )}
    </section>
  );

  if (tourMode) {
    return (
      <SitePageLayout title="Marketplace" description={`Browse verified sellers on ${brand}.`}>
        {body}
      </SitePageLayout>
    );
  }

  return (
    <MarketplacePageFrame title="Marketplace" description={`Browse verified sellers on ${brand}.`}>
      {body}
    </MarketplacePageFrame>
  );
}
